import {
  Outlet,
  Link,
  createRootRoute,
  useRouter,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import "../styles.css";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { deferredGaScript } from "../lib/deferred-ga";
import { SiteHeader, SiteFooter } from "../components/site-chrome";
import { AgentTools } from "../components/agent-tools";

function NotFoundComponent() {
  return (
    <main className="topo flex min-h-[70vh] items-center bg-background px-4">
      <div className="mx-auto max-w-2xl py-24 text-center">
        <p className="label-mono text-primary">Error 404 · Wrong turn</p>
        <h1 className="mt-3 text-7xl font-black sm:text-8xl">Page not found</h1>
        <p className="mt-4 text-muted-foreground">That page doesn't exist or has moved. Try one of these, or call or text 406-799-0095.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/" className="rounded-sm bg-primary px-6 py-3 font-display text-lg font-extrabold uppercase text-primary-foreground">Home</Link>
          <Link to="/services" className="rounded-sm border-2 border-primary px-6 py-3 font-display text-lg font-extrabold uppercase text-primary">Services</Link>
          <Link to="/contact" className="rounded-sm border-2 border-primary px-6 py-3 font-display text-lg font-extrabold uppercase text-primary">Contact</Link>
        </div>
      </div>
    </main>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

function measurementId(): string {
  // Read on the server per request. The client build drops this branch, so the
  // measurement id is not a server-function call in the hydration bundle.
  if (!import.meta.env.SSR) return "";
  return (process.env["GOOGLE_ANALYTICS_MEASUREMENT_ID"] ?? "").trim();
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      // Preload so these fonts are not a second hop on the critical path.
      // crossOrigin is required or the browser downloads each font twice.
      { rel: "preload", as: "font", type: "font/woff2", href: "/fonts/big-shoulders-display-latin.woff2", crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: "/fonts/manrope-latin.woff2", crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: "/fonts/jetbrains-mono-700-latin.woff2", crossOrigin: "anonymous" },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
    ],
    scripts: (() => {
      const ga = deferredGaScript(measurementId());
      return ga ? [{ children: ga }] : [];
    })(),
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <AgentTools />
      <SiteHeader />
      <Outlet />
      <SiteFooter />
    </>
  );
}
